import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaCheckCircle, FaUser, FaClipboard, FaTimes, FaUserShield } from 'react-icons/fa';
import './style.css';
import BottomNavigation from './BottomNavigation';
import LiveTransactionsBot from './LiveTransactionsBot';
import { useAuth } from '../security/security/AuthContext';
import axios from 'axios';
import HomeToastNotification from './auth/loaders/HomeToastNotification';
import CashoutView from './CashoutView';
import HotProductSection from './HotProductSection';
import Rewards from './Rewards';
import WhatsAppFloat from './auth/WhatsAppFloat';
import CertificateView from './CertificateView';
import VisionTechHeader from './VisionTechHeader';
import { Wallet, CreditCard, Gift } from 'lucide-react';
import CertificateClientMoney from './CertificateClientMoney';
import TeslaInvestmentChart from './TeslaInvestmentChart';
import { IoMdCash } from 'react-icons/io';
import { MdAttachMoney, MdCardGiftcard } from 'react-icons/md';
import { X, TrendingUp, Users, Send} from 'lucide-react';
import { MessageCircle } from 'lucide-react';

const Home = () => {
  const [selectedNav, setSelectedNav] = useState('home');
  const [showPopup, setShowPopup] = useState(true);
  const [currentView, setCurrentView] = useState('main');
  const [depositAmount, setDepositAmount] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [openedGiftBoxes, setOpenedGiftBoxes] = useState([]);
  const [showInvestmentDialog, setShowInvestmentDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [products, setProducts] = useState([]);
  const [toastNotification, setToastNotification] = useState(null);
  const [investmentSuccess, setInvestmentSuccess] = useState(null);
  const { userDetails, token, updateUserDetails, isLoading, isSuperLogin, exitSuperLogin } = useAuth();

  // New states for payment processing
  const [showPaymentLoader, setShowPaymentLoader] = useState(false);
  const [paymentLoaderMessage, setPaymentLoaderMessage] = useState('');
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [transactionCode, setTransactionCode] = useState('');
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const [isActivated, setIsActivated] = useState(false);

  const [hotProducts, setHotProducts] = useState([]);

   const [isInvestmentLoading, setIsInvestmentLoading] = useState(false);

  useEffect(() => {
    if (!isLoading && userDetails?.mobile) {
      setPhoneNumber(userDetails.mobile);
    }
    if (!isLoading && userDetails?.email) {
      fetchAvailableProducts();
    }
  }, [userDetails, isLoading]);

  const formatPhoneNumber = (number) => {
    if (number.startsWith('07') || number.startsWith('01')) {
      return '254' + number.slice(1);
    }
    return number;
  };

  
// Refresh user data function
const refreshUserData = async () => {
  try {
    const userResponse = await axios.get(`https://api.visiontech2030.com/api/v1/auth/${userDetails.email}`, {
      headers: {
        'Authorization': token
      }
    });
    updateUserDetails(userResponse.data);
  } catch (error) {
    console.error('Error refreshing user data:', error);
    setToastNotification({ message: 'Failed to refresh user data', type: 'error' });
  }
};


  // Refresh products function
  const refreshProducts = async () => {
    try {
      const response = await axios.get(`https://api.visiontech2030.com/api/products/available/${userDetails.email}`);
      if (response.data && Array.isArray(response.data)) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error('Error refreshing products:', error);
    }
  };


  // Combined refresh function
  const refreshAllData = async () => {
    await Promise.all([
      refreshUserData(),
      refreshProducts()
    ]);
  };

  // Add effect to refresh data when view changes to 'main'
  useEffect(() => {
    if (currentView === 'main') {
      refreshAllData();
    }
  }, [currentView]);


  useEffect(() => {
    fetchAvailableProducts();
  }, [userDetails.email]);

  // Fetch available products
  const fetchAvailableProducts = async () => {
    try {
      const [productsResponse, hotProductsResponse] = await Promise.all([
        axios.get(`https://api.visiontech2030.com/api/products/available/${userDetails.email}`),
        axios.get('https://api.visiontech2030.com/api/products/hot')
      ]);
      
      if (productsResponse.data && Array.isArray(productsResponse.data)) {
        // Filter out HOT products from the regular products list
        const hotProductIds = hotProductsResponse.data.map(product => product.id);
        
        setProducts(productsResponse.data.filter(product => 
          product.productType === 'NORMAL' && !hotProductIds.includes(product.id)
        ));
      }
      
      if (hotProductsResponse.data && hotProductsResponse.data.length > 0) {
        setHotProducts(hotProductsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setToastNotification({ message: 'Failed to fetch products. Please try again.', type: 'error' });
    }
  };


  const handleInvest = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setShowInvestmentDialog(true);
  };

  const handleCloseInvestmentDialog = () => {
    setShowInvestmentDialog(false);
    setSelectedProduct(null);
    setQuantity(1);
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);
  };

  const calculateTotalBalance = () => {
    const wallet = Number(userDetails.wallet) || 0;
    const invitationIncome = Number(userDetails.invitationIncome) || 0;
    return (wallet + invitationIncome).toLocaleString();
  };

   // Update the handleInvestNow function
   const handleInvestNow = async () => {
    try {
      // Set loading state to true when button is clicked
      setIsInvestmentLoading(true);
      
      const response = await axios.post('https://api.visiontech2030.com/api/investments/invest', null, {
        params: {
          userEmail: userDetails.email,
          productId: selectedProduct.id,
          quantity: quantity
        }
      });
      
      if (response.status === 200) {
        await refreshAllData();
        setShowInvestmentDialog(false);
        setInvestmentSuccess(response.data);
        setCurrentView('investmentSuccess');
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        setToastNotification({ message: error.response.data, type: 'error' });
        setCurrentView('deposit');
        setDepositAmount((selectedProduct.price * quantity).toString());
      } else {
        console.error('Error making investment:', error);
        setToastNotification({ message: 'Oops! You are out of units, please try another package.', type: 'error' });
      }
    } finally {
      // Always reset loading state, whether the request succeeds or fails
      setIsInvestmentLoading(false);
    }
  };


   // Render investment success view
    const renderInvestmentSuccessView = () => (
      <div className="vt-success-container">
        <div className="vt-success-card">
          <div className="vt-glow-effect"></div>
          
          <div className="vt-success-icon-wrapper">
            <FaCheckCircle className="vt-success-icon" />
          </div>
          
          <h2 className="vt-success-title">Investment Successful!</h2>
          
          <div className="vt-success-product">
            <span className="vt-product-name">{investmentSuccess.productName}</span>
            <span className="vt-product-quantity">×{quantity}</span>
          </div>
          
          <div className="vt-details-table">
            <div className="vt-table-row">
              <span className="vt-table-label">Invested Amount</span>
              <span className="vt-table-value">KES {investmentSuccess.investedAmount.toLocaleString()}</span>
            </div>
            <div className="vt-table-row">
              <span className="vt-table-label">Daily Income</span>
              <span className="vt-table-value">KES {investmentSuccess.dailyIncome.toLocaleString()}</span>
            </div>
            <div className="vt-table-row highlight">
              <span className="vt-table-label">Total Expected Income</span>
              <span className="vt-table-value">KES {investmentSuccess.totalIncome.toLocaleString()}</span>
            </div>
            <div className="vt-table-row">
              <span className="vt-table-label">Investment Cycle Ends</span>
              <span className="vt-table-value">{investmentSuccess.validUntil.split('T')[0]}</span>
            </div>
          </div>
          
          <div className="vt-info-box">
            <h3 className="vt-info-title">How to Receive Income</h3>
            <ul className="vt-info-list">
              <li>You can receive your investment income every hour on the Order page</li>
              <li>When the investment cycle ends, your principal will automatically return to your balance</li>
            </ul>
          </div>
          
          <div className="vt-action-buttons">
            <button 
              className="vt-primary-btn" 
              onClick={() => window.location.href = '/order'}
            >
              View Orders
            </button>
            <button 
              className="vt-secondary-btn" 
              onClick={() => setCurrentView('main')}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  
  const joinTelegramChannel = () => {
    const telegramLink = "https://t.me/+y-cgsiLhbKBiYjk0";
    window.open(telegramLink, '_blank');
  };

  const joinWhatsAppCommunity = () => {
    const communityLink = "https://chat.whatsapp.com/HdmoHwuRxaWKXb1HujiPYd?mode=wwt";
    window.open(communityLink, '_blank');
  };


  const renderMainView = () => (
    <>
     <div className="home-container">
     <h1 class="investment-title">
        < VisionTechHeader />
      </h1>
        <div className="action-buttons">
          <button className="action-btn" onClick={() => setCurrentView('deposit')}> <Wallet className="vision-tech-icon" /> Deposit</button>
          <button className="action-btn" onClick={() => setCurrentView('cashout')}><CreditCard className="vision-tech-icon" /> Withdraw</button>
          <button className="action-btn" onClick={() => setCurrentView('rewards')}> <Gift className="vision-tech-icon" /> Tokens</button>
        </div>
        <div className="vision-tech-balance-section">
        <div className="vision-tech-balance-content">
          <h4 className="vision-tech-balance-label">Balance</h4>
          <h4 className="vision-tech-balance-amount">
            Ksh. {calculateTotalBalance()}
          </h4>
        </div>
      </div>
        <LiveTransactionsBot />
        <TeslaInvestmentChart />
        <h2 className="section-title-cerificates">Cerificates</h2>
        <header className="home-header">
      <div className="team-illustration" onClick={() => setCurrentView('certificate')}>
      <div className="certificate-preview">
        <div className="preview-content">
          <span>Click to view Certificate of Incorporation</span>
          <div className="preview-certificate">
            <div className="certificate-placeholder">
              <img 
                src="/certificate.PNG"
                alt="Certificate Preview" 
                className="preview-img"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="team-illustration" onClick={() => setCurrentView('clientmoney')}>
      <div className="certificate-preview">
        <div className="preview-content">
          <span>Click to view Client Money Protection</span>
          <div className="preview-certificate">
            <div className="certificate-placeholder">
              <img 
                src="/client_money_protection.PNG"
                alt="Certificate Preview" 
                className="preview-img"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
        </header>
        <h2 className="section-title">Premium Package</h2>
        {hotProducts.length > 0 && (
          <div className="hot-products-container">
            {hotProducts.map(product => (
              <HotProductSection key={product.id} product={product} onInvest={handleInvest} />
            ))}
          </div>
        )}

      </div>
      {showInvestmentDialog && selectedProduct && (
              <div className="vt-investment-overlay">
                <div className="vt-investment-dialog">
                  <div className="vt-dialog-header">
                    <h2>{selectedProduct.name}</h2>
                    <button className="vt-close-btn" onClick={handleCloseInvestmentDialog}>
                      <FaTimes />
                    </button>
                  </div>
                  
                  <div className="vt-price-display">
                    <div className="vt-price-badge">Package</div>
                    <div className="vt-price-label">Investment Price</div>
                    <div className="vt-price-amount">
                      <span className="vt-currency">KES</span>
                      <span className="vt-amount">{selectedProduct.price.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="vt-quantity-selector">
                    <div className="vt-selector-label">Select Quantity</div>
                    <div className="vt-quantity-options">
                      {[...Array(Math.min(selectedProduct.available, 6))].map((_, index) => (
                        <button
                          key={index + 1}
                          className={`vt-quantity-btn ${quantity === index + 1 ? 'active' : ''}`}
                          onClick={() => handleQuantityChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="vt-details-grid">
                    <div className="vt-detail-item">
                      <div className="vt-detail-icon"><IoMdCash /></div>
                      <div className="vt-detail-content">
                        <span className="vt-detail-label">Daily Income</span>
                        <span className="vt-detail-value">KES {(selectedProduct.dailyIncome * quantity).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="vt-detail-item">
                      <div className="vt-detail-icon"><MdAttachMoney /></div>
                      <div className="vt-detail-content">
                        <span className="vt-detail-label">Total Income</span>
                        <span className="vt-detail-value highlight">KES {(selectedProduct.totalIncome * quantity).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="vt-detail-item">
                      <div className="vt-detail-icon"><FaClipboard /></div>
                      <div className="vt-detail-content">
                        <span className="vt-detail-label">Available Units</span>
                        <span className="vt-detail-value">{selectedProduct.available}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="vt-total-section">
                    <span className="vt-total-label">Total Investment Amount</span>
                    <span className="vt-total-value">KES {(selectedProduct.price * quantity).toLocaleString()}</span>
                  </div>
                  
                  <button 
                  className="vt-buy-button" 
                  onClick={handleInvestNow}
                  disabled={isInvestmentLoading}
                >
                  <span className="vt-button-shine"></span>
                  {isInvestmentLoading ? (
                    <>
                      <span className="vt-btn-spinner"></span>
                      Processing...
                    </>
                  ) : 'Buy Now'}
                </button>
                  
                  <div className="vt-investment-info">
                    <p>You can receive your investment income every hour. When the investment cycle is over, your principal will automatically return to your balance and can be withdrawn.</p>
                    <p className="vt-important-note">Note: Some investments need to be received before expiry.</p>
                  </div>
                </div>
              </div>
            )}
          </>
        );


 
  const renderCashoutView = () => (
    <CashoutView 
      setCurrentView={setCurrentView}
      userDetails={userDetails}
      token={token}
      calculateTotalBalance={calculateTotalBalance}
      setToastNotification={setToastNotification}
    />
  );



  const renderRewards = () => (
    <Rewards 
      userEmail={userDetails.email}
      setCurrentView={setCurrentView}
      userDetails={userDetails}
      token={token}
      setToastNotification={setToastNotification}
    />
  );



  // New payment processing functions
   const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setShowPaymentLoader(true);
    setPaymentLoaderMessage('Payment initiated, please check your phone to complete payment.');

    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
    const requestData = {
      PhoneNumber: formattedPhoneNumber,
      Amount: parseInt(depositAmount),
    };

    try {
      await axios.post('https://api.visiontech2030.com/mobile-money/stk-transaction-request', requestData, {
        headers: {
          'Authorization': token
        }
      });
      
      setTimeout(() => {
        setPaymentLoaderMessage('Processing payment...');
      }, 20000);

      setTimeout(() => {
        checkPaymentStatus(formattedPhoneNumber);
      }, 27000);
    } catch (error) {
      console.error(error);
      setShowPaymentLoader(false);
      setToastNotification({ message: 'Failed to initiate payment. Please try again.', type: 'error' });
    }
  };


  const checkPaymentStatus = async (phoneNumber) => {
    try {
      const response = await axios.get(`https://api.visiontech2030.com/mobile-money/transactions/check-payment/${phoneNumber}`, {
        headers: {
          'Authorization': token
        }
      });
  
      if (response.data.paymentFound) {
        await processTransaction(phoneNumber);
        setPaymentSuccess(true);
        setShowPaymentLoader(false);
        setToastNotification({ message: 'Payment successful!', type: 'success' });
        setCurrentView('paymentSuccess');
      } else {
        setShowPaymentLoader(false);
        setShowTransactionForm(true);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setShowPaymentLoader(false);
      setToastNotification({ message: 'Failed to check payment status. Please try again.', type: 'error' });
    }
  };


  // Update the processTransaction function
  const processTransaction = async (phoneNumber) => {
    try {
      const response = await axios.post('https://api.visiontech2030.com/mobile-money/users/activate', {
        phoneNumber: phoneNumber,
        email: userDetails.email
      }, {
        headers: {
          'Authorization': token
        }
      });
  
      if (response.data.message) {
        setIsActivated(true);
        await refreshAllData();
      }
    } catch (error) {
      console.error('Error processing transaction:', error);
      throw error;
    }
  };


  // Update the handleTransactionCodeSubmit function
  const handleTransactionCodeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingPayment(true);
  
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber);
  
    try {
      const verifyResponse = await axios.post('https://api.visiontech2030.com/mobile-money/transactions/verify', {
        phoneNumber: formattedPhoneNumber,
        transactionCode: transactionCode
      }, {
        headers: {
          'Authorization': token
        }
      });
  
      if (verifyResponse.data.verified) {
        await processTransaction(formattedPhoneNumber);
        await refreshAllData();
        setPaymentSuccess(true);
        setCurrentView('paymentSuccess');
        setToastNotification({ message: 'Payment verified successfully!', type: 'success' });
      } else {
        setToastNotification({ message: 'Invalid transaction code. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error verifying transaction:', error);
      setToastNotification({ message: 'Failed to verify transaction. Please try again.', type: 'error' });
    } finally {
      setIsSubmittingPayment(false);
    }
  };



   const renderPaymentProcessView = () => (
      <div className="vt-payment-container">
        <div className="vt-payment-header">
          <button 
            className="vt-back-btn" 
            onClick={() => setCurrentView('deposit')}
          >
            <FaArrowLeft /> <span>Back</span>
          </button>
          <h2 className="vt-payment-title">Complete Payment</h2>
        </div>
  
        {!showPaymentLoader && !showTransactionForm && (
          <div className="vt-payment-summary">
            <div className="vt-summary-header">
              <div className="vt-summary-icon">
                <IoMdCash />
              </div>
              <h3>Payment Details</h3>
            </div>
            
            <div className="vt-summary-content">
              <div className="vt-summary-row">
                <span className="vt-summary-label">Amount</span>
                <span className="vt-summary-value">KES {parseInt(depositAmount).toLocaleString()}</span>
              </div>
              <div className="vt-summary-row">
                <span className="vt-summary-label">Phone Number</span>
                <span className="vt-summary-value">{phoneNumber}</span>
              </div>
              <div className="vt-summary-row">
                <span className="vt-summary-label">Payment Method</span>
                <span className="vt-summary-value">M-Pesa</span>
              </div>
            </div>
            
            <div className="vt-payment-notice">
              <p>You'll receive an M-Pesa prompt on your phone. Please enter your PIN to complete the transaction.</p>
            </div>
            
            <button 
              className="vt-pay-now-btn" 
              onClick={handlePaymentSubmit}
            >
              <span className="vt-button-shine"></span>
              Initiate Payment
            </button>
          </div>
        )}
  
        {showPaymentLoader && (
          <div className="vt-payment-loader">
            <div className="vt-loader-spinner">
              <div className="vt-spinner-inner"></div>
            </div>
            <h3 className="vt-loader-message">{paymentLoaderMessage}</h3>
            
            <div className="vt-loader-steps">
              <div className="vt-step active">
                <div className="vt-step-indicator">1</div>
                <span className="vt-step-text">Payment Initiated</span>
              </div>
              <div className="vt-step-connector"></div>
              <div className="vt-step">
                <div className="vt-step-indicator">2</div>
                <span className="vt-step-text">Check Your Phone</span>
              </div>
              <div className="vt-step-connector"></div>
              <div className="vt-step">
                <div className="vt-step-indicator">3</div>
                <span className="vt-step-text">Processing Payment</span>
              </div>
            </div>
          </div>
        )}
  
        {showTransactionForm && (
          <div className="vt-transaction-form">
            <div className="vt-form-icon">
              <FaClipboard />
            </div>
            
            <h3 className="vt-form-title">Payment Not Found</h3>
            <p className="vt-form-message">If you've already made the payment, please enter your M-PESA transaction code below to verify.</p>
            
            <form onSubmit={handleTransactionCodeSubmit} className="vt-code-form">
              <div className="vt-form-field">
                <label className="vt-field-label">Transaction Code</label>
                <input
                  type="text"
                  value={transactionCode}
                  onChange={(e) => setTransactionCode(e.target.value)}
                  className="vt-code-input"
                  placeholder="e.g. PBI12345678"
                  required
                />
                <p className="vt-field-hint">Find this code in your M-PESA confirmation SMS</p>
              </div>
              
              <button 
                type="submit" 
                className="vt-verify-btn"
                disabled={isSubmittingPayment}
              >
                {isSubmittingPayment ? (
                  <>
                    <span className="vt-btn-spinner"></span>
                    Verifying...
                  </>
                ) : 'Verify Payment'}
              </button>
            </form>
          </div>
        )}
      </div>
    );

  // Update the renderPaymentSuccessView function
  const renderPaymentSuccessView = () => (
      <div className="vt-success-container">
        <div className="vt-success-payment-card">
          <div className="vt-success-glow"></div>
          <div className="vt-payment-success-icon">
            <FaCheckCircle />
          </div>
          
          <h2 className="vt-success-payment-title">Payment Successful!</h2>
          
          <div className="vt-success-amount">
            <span className="vt-success-currency">KES</span>
            <span className="vt-success-value">{parseInt(depositAmount).toLocaleString()}</span>
          </div>
          
          <p className="vt-success-message">
            Your payment has been processed successfully and your account has been credited with the deposited amount.
          </p>
          
          {isActivated && (
            <div className="vt-activation-notice">
              <FaCheckCircle className="vt-activation-icon" />
              <span>Account successfully recharged</span>
            </div>
          )}
          
          <button 
            className="vt-continue-home-btn"
            onClick={async () => {
              await refreshAllData();
              setCurrentView('main');
              setDepositAmount('');
              setPaymentSuccess(false);
              setShowTransactionForm(false);
            }}
          >
            <span className="vt-button-shine"></span>
            Continue to Home
          </button>
        </div>
      </div>
    );



  const renderDepositView = () => (
      <div className="vt-deposit-container">
        <div className="vt-deposit-header">
          <button 
            className="vt-back-btn" 
            onClick={() => setCurrentView('main')}
          >
            <FaArrowLeft /> <span>Back</span>
          </button>
          <h2 className="vt-deposit-title">Deposit Funds</h2>
        </div>
        
        <div className="vt-amount-selector">
          <h3 className="vt-selector-title">Select Amount</h3>
          
          <div className="vt-amount-chips">
            {[500, 1000, 5000, 12000, 25000, 100000].map((amount) => (
              <button
                key={amount}
                className={`vt-amount-chip ${depositAmount === amount.toString() ? 'active' : ''}`}
                onClick={() => setDepositAmount(amount.toString())}
              >
                {amount.toLocaleString()}
              </button>
            ))}
          </div>
  
          <div className="vt-custom-amount">
            <label className="vt-amount-label">Or Enter Custom Amount</label>
            <div className="vt-input-wrapper">
              <span className="vt-currency-prefix">KES</span>
              <input
                type="number"
                placeholder="Minimum: 200"
                value={depositAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  setDepositAmount(value);
                  if (toastNotification) {
                    setToastNotification(null);
                  }
                }}
                className="vt-amount-input"
              />
            </div>
            <p className="vt-amount-hint">Minimum deposit amount: KES 200</p>
          </div>
        </div>
        
        <div className="vt-payment-method">
          <h3 className="vt-method-title">Payment Method</h3>
          <div className="vt-method-card">
            <div className="vt-method-content">
              <img src="/ic_mpesa.png" alt="Mpesa" className="vt-method-logo" />
              <div className="vt-method-info">
                <span className="vt-method-name">M-Pesa</span>
                <span className="vt-method-desc">Fast and secure mobile payment</span>
              </div>
            </div>
            <div className="vt-method-check">
              <FaCheckCircle />
            </div>
          </div>
        </div>
        
        <button 
          className="vt-continue-btn" 
          onClick={() => {
            if (!depositAmount || parseFloat(depositAmount) < 200) {
              setToastNotification({ 
                message: 'Minimum deposit amount is KES 200', 
                type: 'error' 
              });
              return;
            }
            setCurrentView('paymentProcess');
          }}
          disabled={!depositAmount}
        >
          Continue to Payment
        </button>
      </div>
    );

  const SuperLoginBanner = () => {
      if (!isSuperLogin) return null;
      return (
        <div className="vt-admin-banner">
          <div className="vt-admin-content">
            <FaUserShield className="vt-admin-icon" />
            <span className="vt-admin-text">Admin Super Login Mode</span>
          </div>
          <button 
            className="vt-admin-exit-btn"
            onClick={exitSuperLogin}
          >
            Exit Super Login
          </button>
        </div>
      );
    };



  const renderView = () => {
    switch (currentView) {
      case 'deposit':
        return renderDepositView();
      case 'paymentProcess':
        return renderPaymentProcessView();
      case 'paymentSuccess':
        return renderPaymentSuccessView();
      case 'cashout':
        return renderCashoutView();
      case 'rewards':
        return renderRewards();
      case 'investmentSuccess':
        return renderInvestmentSuccessView();
      case 'certificate':
        return <CertificateView setCurrentView={setCurrentView} />;
      case 'clientmoney':
        return <CertificateClientMoney setCurrentView={setCurrentView} />;
      default:
        return renderMainView();
    }
  };

  return (
    <div className="home-container">
       <SuperLoginBanner />
      {renderView()}
      {showPopup && (
  <div className="visiontech-popup-improve-overlay" onClick={(e) => {
    if (e.target.className === 'visiontech-popup-improve-overlay') setShowPopup(false);
  }}>
    <div className="visiontech-popup-improve-container">
      <button className="visiontech-popup-improve-close-btn" onClick={() => setShowPopup(false)}>
        <X size={18} strokeWidth={2.5} />
      </button>
      
      <div className="visiontech-popup-improve-features">
        <div className="visiontech-popup-improve-feature">
          <div className="feature-icon-wrapper">
            <TrendingUp size={20} className="feature-icon" />
          </div>
          <p className="feature-main">Don't miss out on what's happening!</p>
          <p className="feature-subtitle">
            <strong>We value your growth and success — stay informed and stay rewarded!</strong>
          </p>
        </div>
      </div>
      <div className="visiontech-popup-improve-actions">
        <button className="visiontech-popup-improve-action-btn" onClick={joinWhatsAppCommunity}>
          <span className="btn-content">
            <MessageCircle size={18} className="btn-icon" />
            <span className="btn-text">Join WhatsApp Community</span>
          </span>
          <span className="btn-glow"></span>
        </button>
      </div>
      <div className="visiontech-popup-improve-actions">
        <button className="visiontech-popup-improve-action-btn" onClick={joinTelegramChannel}>
          <span className="btn-content">
            <Send size={18} className="btn-icon" />
            <span className="btn-text">Join Telegram Channel</span>
          </span>
          <span className="btn-glow"></span>
        </button>
      </div>
    </div>
  
    <style jsx>{`
      .visiontech-popup-improve-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        padding: 1rem;
        animation: overlayFadeIn 0.5s ease-out;
      }

      @keyframes overlayFadeIn {
        from { 
          opacity: 0;
          backdrop-filter: blur(0px);
        }
        to { 
          opacity: 1;
          backdrop-filter: blur(16px);
        }
      }

      .visiontech-popup-improve-container {
        background: linear-gradient(145deg, #0a0f1c 0%, #162332 50%, #0d1421 100%);
        border-radius: 32px;
        padding: 2.5rem;
        width: 100%;
        max-width: 460px;
        min-height: 320px;
        position: relative;
        box-shadow: 
          0 25px 80px -15px rgba(0, 0, 0, 0.7),
          0 0 0 1px rgba(255, 255, 255, 0.1),
          inset 0 2px 0 rgba(255, 255, 255, 0.15),
          inset 0 -1px 0 rgba(0, 0, 0, 0.3);
        animation: containerSlideUp 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        border: 1px solid rgba(255, 255, 255, 0.15);
        overflow: hidden;
        margin: auto;
      }

      .visiontech-popup-improve-container::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: linear-gradient(90deg, 
          transparent 0%, 
          rgba(245, 185, 66, 0.3) 20%, 
          rgba(245, 185, 66, 0.8) 50%, 
          rgba(245, 185, 66, 0.3) 80%, 
          transparent 100%);
        animation: topGlow 2s ease-in-out infinite alternate;
      }

      .visiontech-popup-improve-container::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 120%;
        height: 120%;
        background: radial-gradient(circle, rgba(245, 185, 66, 0.03) 0%, transparent 70%);
        transform: translate(-50%, -50%);
        pointer-events: none;
        z-index: -1;
      }

      @keyframes topGlow {
        from { opacity: 0.5; }
        to { opacity: 1; }
      }

      @keyframes containerSlideUp {
        from {
          opacity: 0;
          transform: translateY(50px) scale(0.9);
          filter: blur(5px);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
          filter: blur(0px);
        }
      }

      .visiontech-popup-improve-close-btn {
        position: absolute;
        top: 1.5rem;
        right: 1.5rem;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: none;
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.7);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        backdrop-filter: blur(15px);
        border: 1px solid rgba(255, 255, 255, 0.12);
        z-index: 10;
      }

      .visiontech-popup-improve-close-btn:hover {
        background: rgba(255, 255, 255, 0.15);
        transform: rotate(90deg) scale(1.1);
        color: rgba(255, 255, 255, 1);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
        border-color: rgba(255, 255, 255, 0.2);
      }

      .visiontech-popup-improve-features {
        margin-bottom: 2.5rem;
        text-align: center;
      }

      .visiontech-popup-improve-feature {
        background: linear-gradient(135deg, 
          rgba(245, 185, 66, 0.1) 0%, 
          rgba(245, 185, 66, 0.05) 50%, 
          rgba(245, 185, 66, 0.02) 100%);
        border: 1px solid rgba(245, 185, 66, 0.25);
        border-radius: 20px;
        padding: 2rem 1.5rem;
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
      }

      .visiontech-popup-improve-feature::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, 
          transparent 0%, 
          rgba(245, 185, 66, 0.15) 50%, 
          transparent 100%);
        transition: 0.8s ease;
      }

      .visiontech-popup-improve-feature:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 40px rgba(245, 185, 66, 0.2);
        border-color: rgba(245, 185, 66, 0.4);
      }

      .visiontech-popup-improve-feature:hover::before {
        left: 100%;
      }

      .feature-icon-wrapper {
        display: flex;
        justify-content: center;
        margin-bottom: 1rem;
      }

      .feature-icon {
        color: #f5b942;
        animation: pulse 2s ease-in-out infinite;
      }

      @keyframes pulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.1); opacity: 0.8; }
      }

      .feature-main {
        color: rgba(255, 255, 255, 0.95);
        margin: 0 0 1rem 0;
        font-size: 1.1rem;
        line-height: 1.5;
        font-weight: 600;
      }

      .feature-subtitle {
        color: rgba(255, 255, 255, 0.8);
        margin: 0;
        font-size: 0.95rem;
        line-height: 1.6;
        font-weight: 400;
      }

      .feature-subtitle strong {
        color: #f5b942;
        font-weight: 700;
      }

      .visiontech-popup-improve-actions {
        display: flex;
        justify-content: center;
        margin-bottom: 1.2rem;
      }

      .visiontech-popup-improve-actions:last-child {
        margin-bottom: 0;
      }

      .visiontech-popup-improve-action-btn,
      .visiontech-popup-community-action-btn {
        padding: 1.3rem 2.5rem;
        border-radius: 50px;
        border: none;
        font-weight: 700;
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
        min-width: 240px;
        letter-spacing: 0.3px;
        text-transform: uppercase;
        backdrop-filter: blur(15px);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      }

      .visiontech-popup-improve-action-btn {
        background: linear-gradient(135deg, #1ef130 0%, #3cb3c8 30%, #00d4ff 70%, #1ef130 100%);
        background-size: 200% 200%;
        color: #1a1a1a;
        border: 1px solid rgba(255, 255, 255, 0.2);
        animation: gradientFlow 3s ease-in-out infinite;
      }

      .visiontech-popup-community-action-btn {
        background: linear-gradient(135deg,rgb(143, 41, 184) 0%,rgb(44, 223, 20) 30%,rgb(5, 245, 57) 70%,rgb(216, 62, 216) 100%);
        background-size: 200% 200%;
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.2);
        animation: gradientFlow 3s ease-in-out infinite reverse;
      }

      @keyframes gradientFlow {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }

      .btn-content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.7rem;
        position: relative;
        z-index: 2;
      }

      .btn-icon {
        transition: all 0.3s ease;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
      }

      .btn-text {
        font-weight: 700;
        letter-spacing: 0.3px;
        font-size: 0.9rem;
      }

      .btn-glow {
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, 
          transparent 0%, 
          rgba(255, 255, 255, 0.4) 50%, 
          transparent 100%);
        transition: 0.7s ease;
        z-index: 1;
      }

      .visiontech-popup-improve-action-btn:hover,
      .visiontech-popup-community-action-btn:hover {
        transform: translateY(-6px) scale(1.03);
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
      }

      .visiontech-popup-improve-action-btn:hover {
        box-shadow: 0 20px 60px rgba(245, 185, 66, 0.5);
      }

      .visiontech-popup-community-action-btn:hover {
        box-shadow: 0 20px 60px rgba(31, 241, 48, 0.5);
      }

      .visiontech-popup-improve-action-btn:hover .btn-glow,
      .visiontech-popup-community-action-btn:hover .btn-glow {
        left: 100%;
      }

      .visiontech-popup-improve-action-btn:hover .btn-icon,
      .visiontech-popup-community-action-btn:hover .btn-icon {
        transform: scale(1.2) rotate(5deg);
      }

      .visiontech-popup-improve-action-btn:active,
      .visiontech-popup-community-action-btn:active {
        transform: translateY(-3px) scale(0.98);
      }

      /* Perfect centering for all screen sizes */
      @media (max-width: 768px) {
        .visiontech-popup-improve-overlay {
          padding: 0.8rem;
        }

        .visiontech-popup-improve-container {
          padding: 2rem 1.5rem;
          max-width: 420px;
          border-radius: 28px;
          min-height: auto;
        }

        .visiontech-popup-improve-close-btn {
          top: 1.2rem;
          right: 1.2rem;
          width: 40px;
          height: 40px;
        }

        .visiontech-popup-improve-feature {
          padding: 1.5rem 1.2rem;
        }

        .feature-main {
          font-size: 1rem;
        }

        .feature-subtitle {
          font-size: 0.9rem;
        }

        .visiontech-popup-improve-action-btn,
        .visiontech-popup-community-action-btn {
          padding: 1.1rem 2rem;
          font-size: 0.85rem;
          min-width: 200px;
        }

        .btn-content {
          gap: 0.6rem;
        }

        .btn-text {
          font-size: 0.85rem;
        }
      }

      @media (max-width: 480px) {
        .visiontech-popup-improve-overlay {
          padding: 0.5rem;
        }

        .visiontech-popup-improve-container {
          padding: 1.8rem 1.2rem;
          max-width: 95%;
          border-radius: 24px;
        }

        .visiontech-popup-improve-close-btn {
          top: 1rem;
          right: 1rem;
          width: 36px;
          height: 36px;
        }

        .visiontech-popup-improve-feature {
          padding: 1.2rem 1rem;
        }

        .feature-main {
          font-size: 0.95rem;
        }

        .feature-subtitle {
          font-size: 0.85rem;
        }

        .visiontech-popup-improve-action-btn,
        .visiontech-popup-community-action-btn {
          padding: 1rem 1.5rem;
          font-size: 0.8rem;
          min-width: 180px;
        }

        .btn-content {
          gap: 0.5rem;
        }

        .btn-text {
          font-size: 0.8rem;
        }

        .visiontech-popup-improve-features {
          margin-bottom: 2rem;
        }

        .visiontech-popup-improve-actions {
          margin-bottom: 1rem;
        }
      }

      @media (max-width: 360px) {
        .visiontech-popup-improve-container {
          max-width: 98%;
          padding: 1.5rem 1rem;
        }

        .visiontech-popup-improve-action-btn,
        .visiontech-popup-community-action-btn {
          min-width: 160px;
          padding: 0.9rem 1.2rem;
        }
      }

      /* Landscape mobile optimization */
      @media (max-height: 600px) and (orientation: landscape) {
        .visiontech-popup-improve-overlay {
          align-items: flex-start;
          padding-top: 2rem;
          overflow-y: auto;
        }

        .visiontech-popup-improve-container {
          margin: auto;
          max-height: 90vh;
          overflow-y: auto;
        }
      }
    `}</style>
  </div>
)}
       <BottomNavigation />
       {toastNotification && (
         <HomeToastNotification
           message={toastNotification.message}
           type={toastNotification.type}
           onClose={() => setToastNotification(null)}
         />
       )}
       <WhatsAppFloat />
       <style jsx>{`
        .vt-root {
          position: relative;
          min-height: 100vh;
          padding-bottom: 80px;
          background: linear-gradient(135deg, #0c1631 0%, #1a2c56 100%);
          color: #fff;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        /* ======== MAIN VIEW STYLES ======== */
    
        
        .premium-section-header {
          text-align: center;
          margin-bottom: 32px;
          position: relative;
        }
        
        .premium-title {
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 12px;
          color: #e2e8f0;
          position: relative;
          display: inline-block;
          background: linear-gradient(to right, #fff, #a5b4fc);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        
        .title-underline {
          height: 3px;
          width: 80px;
          background: linear-gradient(to right, #4f46e5, #7e3af2);
          margin: 0 auto;
          border-radius: 2px;
        }
        
        .hot-products-container {
          margin-bottom: 30px;
        }
        
        /* ======== INVESTMENT DIALOG STYLES ======== */
        .vt-investment-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .vt-investment-dialog {
          background: rgba(15, 23, 42, 0.95);
          border-radius: 16px;
          max-width: 480px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          padding: 24px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          animation: scaleIn 0.3s ease;
          position: relative;
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .vt-dialog-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .vt-dialog-header h2 {
          font-size: 20px;
          font-weight: 600;
          margin: 0;
          color: #fff;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .vt-close-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
          color: #fff;
          transform: rotate(90deg);
        }
        
        .vt-price-display {
          position: relative;
          background: linear-gradient(135deg, rgba(79, 70, 229, 0.2), rgba(124, 58, 237, 0.2));
          border-radius: 12px;
          padding: 20px 16px 16px;
          margin-bottom: 20px;
          text-align: center;
          border: 1px solid rgba(79, 70, 229, 0.3);
          overflow: hidden;
        }
        
        .vt-price-badge {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%) translateY(-50%);
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: white;
          font-size: 10px;
          font-weight: 600;
          padding: 4px 12px;
          border-radius: 20px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          box-shadow: 0 2px 6px rgba(79, 70, 229, 0.4);
        }
        
        .vt-price-label {
          display: block;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 8px;
        }
        
        .vt-price-amount {
          display: flex;
          align-items: baseline;
          justify-content: center;
        }
        
        .vt-currency {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.7);
          margin-right: 6px;
        }
        
        .vt-amount {
          font-size: 32px;
          font-weight: 700;
          color: #fff;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .vt-quantity-selector {
          margin-bottom: 20px;
        }
        
        .vt-selector-label {
          font-size: 16px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 12px;
        }
        
        .vt-quantity-options {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
          gap: 8px;
        }
        
        .vt-quantity-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          height: 44px;
          font-size: 16px;
          color: #fff;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-quantity-btn:hover:not(.active) {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }
        
        .vt-quantity-btn.active {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          border-color: transparent;
          box-shadow: 0 4px 10px rgba(79, 70, 229, 0.3);
        }
        
        .vt-details-grid {
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          margin-bottom: 20px;
          padding: 12px;
          overflow: hidden;
        }
        
        .vt-detail-item {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .vt-detail-icon {
          width: 36px;
          height: 36px;
          background: rgba(79, 70, 229, 0.15);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a5b4fc;
          font-size: 18px;
          flex-shrink: 0;
        }
        
        .vt-detail-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .vt-detail-label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }
        
        .vt-detail-value {
          font-size: 15px;
          font-weight: 600;
          color: #fff;
        }
        
        .vt-detail-value.highlight {
          color: #22d3ee;
          font-size: 16px;
        }
        
        .vt-total-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: rgba(79, 70, 229, 0.1);
          border-radius: 10px;
          padding: 14px 16px;
          margin-bottom: 20px;
          border-left: 3px solid #4f46e5;
        }
        
        .vt-total-label {
          font-size: 15px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
        }
        
        .vt-total-value {
          font-size: 18px;
          font-weight: 700;
          color: #a5b4fc;
        }
        
        .vt-button-shine {
          position: absolute;
          top: -180px;
          left: -230px;
          width: 100px;
          height: 500px;
          background: rgba(255, 255, 255, 0.1);
          transform: rotate(45deg);
          transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          pointer-events: none;
        }
        
        .vt-buy-button {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 14px;
          font-size: 16px;
          font-weight: 600;
          width: 100%;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 20px;
          box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3);
        }
        
        .vt-buy-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4);
        }
        
        .vt-buy-button:hover .vt-button-shine {
          transform: translateX(900px) rotate(45deg);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .vt-investment-info {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 10px;
          padding: 16px;
          font-size: 13px;
          line-height: 1.5;
        }
        
        .vt-investment-info p {
          margin: 0 0 8px;
          color: rgba(255, 255, 255, 0.7);
        }
        
        .vt-investment-info p:last-child {
          margin-bottom: 0;
        }
        
        .vt-important-note {
          color: #fcd34d !important;
          font-weight: 500;
        }
        
        /* ======== SUCCESS VIEW STYLES ======== */
        .vt-success-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 70vh;
          padding: 20px 16px;
        }
        
        .vt-success-card {
          position: relative;
          background: rgba(15, 23, 42, 0.8);
          border-radius: 16px;
          padding: 24px;
          max-width: 500px;
          width: 100%;
          text-align: center;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          overflow: hidden;
        }
        
        .vt-glow-effect {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(79, 70, 229, 0.15) 0%, transparent 70%);
          top: -150px;
          right: -150px;
          z-index: 0;
        }
        
        .vt-success-icon-wrapper {
          position: relative;
          z-index: 1;
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: rgba(52, 211, 153, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          box-shadow: 0 0 20px rgba(52, 211, 153, 0.3);
        }
        
        .vt-success-icon {
          font-size: 32px;
          color: #34d399;
        }
        
        .vt-success-title {
          position: relative;
          z-index: 1;
          font-size: 24px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 24px;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .vt-success-product {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 24px;
        }
        
        .vt-product-name {
          font-size: 18px;
          font-weight: 600;
          color: #fff;
        }
        
        .vt-product-quantity {
          background: rgba(79, 70, 229, 0.2);
          color: #a5b4fc;
          font-size: 14px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 20px;
        }
        
        .vt-details-table {
          position: relative;
          z-index: 1;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .vt-table-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .vt-table-row:last-child {
          border-bottom: none;
        }
        
        .vt-table-row.highlight {
          background: rgba(52, 211, 153, 0.1);
        }
        
        .vt-table-row.highlight .vt-table-value {
          color: #34d399;
          font-weight: 700;
        }
        
        .vt-table-label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          text-align: left;
        }
        
        .vt-table-value {
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          text-align: right;
        }
        
        .vt-info-box {
          position: relative;
          z-index: 1;
          background: rgba(79, 70, 229, 0.1);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 24px;
          text-align: left;
          border: 1px solid rgba(79, 70, 229, 0.2);
        }
        
        .vt-info-title {
          font-size: 16px;
          font-weight: 600;
          color: #fff;
          margin: 0 0 12px;
        }
        
        .vt-info-list {
          margin: 0;
          padding-left: 20px;
        }
        
        .vt-info-list li {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 8px;
          line-height: 1.5;
        }
        
        .vt-info-list li:last-child {
          margin-bottom: 0;
        }
        
        .vt-action-buttons {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        
        .vt-primary-btn {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }
        
        .vt-primary-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-secondary-btn {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          padding: 12px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-secondary-btn:hover {
          background: rgba(255, 255, 255, 0.15);
        }
        
        /* ======== DEPOSIT VIEW STYLES ======== */
        .vt-deposit-container {
          padding: 20px 16px;
          max-width: 600px;
          margin-bottom: 3rem;
        }
        
        .vt-deposit-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .vt-back-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: #fff;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-back-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          transform: translateX(-2px);
        }
        
        .vt-deposit-title {
          font-size: 22px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }
        
        .vt-amount-selector {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .vt-selector-title {
          font-size: 18px;
          font-weight: 600;
          color: #fff;
          margin: 0 0 16px;
        }
        
        .vt-amount-chips {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          margin-bottom: 20px;
        }
        
        .vt-amount-chip {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #fff;
          border-radius: 8px;
          padding: 12px 8px;
          font-size: 14px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-amount-chip:hover:not(.active) {
          background: rgba(255, 255, 255, 0.1);
          transform: translateY(-2px);
        }
        
        .vt-amount-chip.active {
          background: rgba(79, 70, 229, 0.2);
          border-color: rgba(79, 70, 229, 0.4);
          color: #a5b4fc;
        }
        
        .vt-custom-amount {
          margin-top: 20px;
        }
        
        .vt-amount-label {
          display: block;
          font-size: 15px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 8px;
        }
        
        .vt-input-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 0 16px;
          overflow: hidden;
          transition: all 0.2s ease;
        }
        
        .vt-input-wrapper:focus-within {
          border-color: rgba(79, 70, 229, 0.5);
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
        }
        
        .vt-currency-prefix {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.6);
          margin-right: 8px;
        }
        
        .vt-amount-input {
          background: transparent;
          border: none;
          color: #fff;
          font-size: 16px;
          padding: 12px 0;
          width: 100%;
          outline: none;
        }
        
        .vt-amount-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }
        
        .vt-amount-hint {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          margin: 8px 0 0;
        }
        
        .vt-payment-method {
          margin-bottom: 24px;
        }
        
        .vt-method-title {
          font-size: 18px;
          font-weight: 600;
          color: #fff;
          margin: 0 0 16px;
        }
        
        .vt-method-card {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          transition: all 0.2s ease;
        }
        
        .vt-method-card:hover {
          background: rgba(255, 255, 255, 0.08);
          transform: translateY(-2px);
        }
        
        .vt-method-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        
        .vt-method-logo {
          width: 40px;
          height: 40px;
          object-fit: contain;
          border-radius: 8px;
        }
        
        .vt-method-info {
          display: flex;
          flex-direction: column;
        }
        
        .vt-method-name {
          font-size: 16px;
          font-weight: 600;
          color: #fff;
          margin-bottom: 4px;
        }
        
        .vt-method-desc {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.6);
        }
        
        .vt-method-check {
          color: #34d399;
          font-size: 20px;
        }
        
        .vt-continue-btn {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          border: none;
          width: 100%;
          padding: 14px;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-continue-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-continue-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        /* ======== PAYMENT PROCESS STYLES ======== */
        .vt-payment-container {
          padding: 20px 16px;
          max-width: 600px;
          margin: 0 auto;
        }
        
        .vt-payment-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .vt-payment-title {
          font-size: 22px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }
        
        .vt-payment-summary {
          background: rgba(15, 23, 42, 0.7);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-summary-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }
        
        .vt-summary-icon {
          width: 40px;
          height: 40px;
          background: rgba(79, 70, 229, 0.1);
          color: #a5b4fc;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        
        .vt-summary-header h3 {
          font-size: 18px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }
        
        .vt-summary-content {
          margin-bottom: 20px;
        }
        
        .vt-summary-row {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-summary-row:last-child {
          border-bottom: none;
        }
        
        .vt-summary-label {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }
        
        .vt-summary-value {
          font-size: 14px;
          font-weight: 600;
          color: #fff;
        }
        
        .vt-payment-notice {
          background: rgba(79, 70, 229, 0.1);
          border-radius: 10px;
          padding: 12px 16px;
          margin-bottom: 20px;
          border-left: 3px solid #4f46e5;
        }
        
        .vt-payment-notice p {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          line-height: 1.5;
        }
        
        .vt-pay-now-btn {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          border: none;
          width: 100%;
          padding: 14px;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-pay-now-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-pay-now-btn:hover .vt-button-shine {
          transform: translateX(900px) rotate(45deg);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .vt-payment-loader {
          background: rgba(15, 23, 42, 0.7);
          border-radius: 16px;
          padding: 30px 24px;
          text-align: center;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-loader-spinner {
          width: 60px;
          height: 60px;
          margin: 0 auto 20px;
          position: relative;
        }
        
        .vt-spinner-inner {
          position: absolute;
          width: 100%;
          height: 100%;
          border: 3px solid transparent;
          border-top-color: #4f46e5;
          border-radius: 50%;
          animation: vt-spinner-rotation 1.5s linear infinite;
        }
        
        .vt-spinner-inner::before,
        .vt-spinner-inner::after {
          content: '';
          position: absolute;
          border: 3px solid transparent;
          border-radius: 50%;
        }
        
        .vt-spinner-inner::before {
          top: 5px;
          left: 5px;
          right: 5px;
          bottom: 5px;
          border-top-color: #7e3af2;
          animation: vt-spinner-rotation 2s linear infinite;
        }
        
        .vt-spinner-inner::after {
          top: 12px;
          left: 12px;
          right: 12px;
          bottom: 12px;
          border-top-color: #a5b4fc;
          animation: vt-spinner-rotation 1s linear infinite;
        }
        
        @keyframes vt-spinner-rotation {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        .vt-loader-message {
          font-size: 18px;
          font-weight: 600;
          color: #fff;
          margin: 0 0 30px;
        }
        
        .vt-loader-steps {
          display: flex;
          justify-content: space-between;
          margin: 0 auto;
          max-width: 400px;
          position: relative;
        }
        
        .vt-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 1;
          width: 33.33%;
        }

        .vt-buy-button:disabled {
            opacity: 0.7;
            cursor: wait;
            position: relative;
          }

          .vt-buy-button .vt-btn-spinner {
            display: inline-block;
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            border-top-color: #fff;
            animation: vt-spinner-rotation 1s linear infinite;
            margin-right: 8px;
            vertical-align: middle;
          }

          .vt-buy-button:disabled .vt-button-shine {
            display: none;
          }
        
        .vt-step-indicator {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          color: rgba(255, 255, 255, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 8px;
          transition: all 0.3s ease;
        }
        
        .vt-step.active .vt-step-indicator {
          background: #4f46e5;
          border-color: #7e3af2;
          color: #fff;
          box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.2);
        }
        
        .vt-step-connector {
          position: absolute;
          top: 15px;
          left: 16.66%;
          right: 16.66%;
          height: 2px;
          background: rgba(255, 255, 255, 0.1);
          z-index: 0;
        }
        
        .vt-step-text {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          text-align: center;
          max-width: 80px;
        }
        
        .vt-step.active .vt-step-text {
          color: rgba(255, 255, 255, 0.9);
        }
        
        .vt-transaction-form {
          background: rgba(15, 23, 42, 0.7);
          border-radius: 16px;
          padding: 24px;
          text-align: center;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .vt-form-icon {
          width: 60px;
          height: 60px;
          background: rgba(251, 191, 36, 0.1);
          color: #fbbf24;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          margin: 0 auto 16px;
        }
        
        .vt-form-title {
          font-size: 20px;
          font-weight: 600;
          color: #fff;
          margin: 0 0 8px;
        }
        
        .vt-form-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 24px;
          line-height: 1.5;
        }
        
        .vt-code-form {
          text-align: left;
        }
        
        .vt-form-field {
          margin-bottom: 24px;
        }
        
        .vt-field-label {
          display: block;
          font-size: 15px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 8px;
        }
        
        .vt-code-input {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          padding: 12px 16px;
          color: #fff;
          font-size: 16px;
          outline: none;
          transition: all 0.2s ease;
        }
        
        .vt-code-input:focus {
          border-color: rgba(79, 70, 229, 0.5);
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
        }
        
        .vt-code-input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }
        
        .vt-field-hint {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          margin: 8px 0 0;
        }
        
        .vt-verify-btn {
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          border: none;
          width: 100%;
          padding: 14px;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .vt-verify-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-verify-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .vt-btn-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #fff;
          animation: vt-spinner-rotation 1s linear infinite;
        }
        
        /* ======== PAYMENT SUCCESS STYLES ======== */
        .vt-success-payment-card {
          position: relative;
          background: rgba(15, 23, 42, 0.8);
          border-radius: 16px;
          padding: 30px 24px;
          max-width: 450px;
          width: 100%;
          text-align: center;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          overflow: hidden;
        }
        
        .vt-success-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 8px;
          background: linear-gradient(to right, #4f46e5, #7e3af2);
        }
        
        .vt-payment-success-icon {
          width: 80px;
          height: 80px;
          background: rgba(52, 211, 153, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          font-size: 36px;
          color: #34d399;
          border: 2px solid rgba(52, 211, 153, 0.3);
        }
        
        .vt-success-payment-title {
          font-size: 24px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 16px;
        }
        
        .vt-success-amount {
          display: inline-flex;
          align-items: baseline;
          margin-bottom: 24px;
        }
        
        .vt-success-currency {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.7);
          margin-right: 4px;
        }
        
        .vt-success-value {
          font-size: 30px;
          font-weight: 700;
          color: #fff;
        }
        
        .vt-success-message {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.8);
          margin: 0 0 24px;
          line-height: 1.5;
        }
        
        .vt-activation-notice {
          background: rgba(52, 211, 153, 0.1);
          border-radius: 10px;
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 24px;
          border: 1px solid rgba(52, 211, 153, 0.3);
        }
        
        .vt-activation-icon {
          color: #34d399;
          font-size: 18px;
        }
        
        .vt-activation-notice span {
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          font-weight: 500;
        }
        
        .vt-continue-home-btn {
          position: relative;
          overflow: hidden;
          background: linear-gradient(135deg, #4f46e5, #7e3af2);
          color: #fff;
          border: none;
          width: 100%;
          padding: 14px;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-continue-home-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
        }
        
        .vt-continue-home-btn:hover .vt-button-shine {
          transform: translateX(900px) rotate(45deg);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        /* ======== ADMIN BANNER STYLES ======== */
        .vt-admin-banner {
          background: rgba(239, 68, 68, 0.1);
          border-bottom: 1px solid rgba(239, 68, 68, 0.3);
          padding: 10px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          z-index: 10;
        }
        
        .vt-admin-content {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .vt-admin-icon {
          color: #f87171;
          font-size: 18px;
        }
        
        .vt-admin-text {
          font-size: 14px;
          font-weight: 600;
          color: #f87171;
        }
        
        .vt-admin-exit-btn {
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.4);
          color: #f87171;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .vt-admin-exit-btn:hover {
          background: rgba(239, 68, 68, 0.3);
        }
        
        /* ======== RESPONSIVE STYLES ======== */
        @media (max-width: 640px) {
          .vt-action-buttons {
            grid-template-columns: 1fr;
          }
          
          .vt-amount-chips {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .vt-loader-steps {
            flex-direction: column;
            align-items: flex-start;
            gap: 20px;
          }
          
          .premium-title {
            font-size: 24px;
          }
          
          .vt-step {
            width: 100%;
            flex-direction: row;
            align-items: center;
            gap: 12px;
          }
          
          .vt-step-indicator {
            margin-bottom: 0;
          }
          
          .vt-step-connector {
            top: 50%;
            left: 15px;
            width: 2px;
            height: 20px;
            transform: rotate(90deg);
          }
          
          .vt-step-text {
            max-width: none;
            text-align: left;
          }
        }
        
        @media (max-width: 480px) {
          .vt-investment-dialog {
            padding: 20px 16px;
            max-height: 80vh;
          }
          
          .vt-success-icon-wrapper,
          .vt-payment-success-icon {
            width: 60px;
            height: 60px;
            font-size: 28px;
          }
          
          .vt-success-title,
          .vt-success-payment-title {
            font-size: 20px;
          }
          
          .vt-success-value {
            font-size: 24px;
          }
          
          .vt-deposit-header,
          .vt-payment-header {
            margin-bottom: 16px;
          }
          
          .vt-deposit-title,
          .vt-payment-title {
            font-size: 18px;
          }
          
          .vt-selector-title,
          .vt-method-title,
          .vt-summary-header h3 {
            font-size: 16px;
          }

          .vt-amount {
            font-size: 24px;
          }

          .vt-detail-item {
            flex-direction: row;
            align-items: center;
          }

          .vt-detail-icon {
            width: 32px;
            height: 32px;
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};
 
 export default Home;
